import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { userApi } from 'api';
import { PATHS } from 'routes/paths';
import { personalDataStore } from 'stores/personalDataStore';
import { isValidRuPhone } from 'utils/phone';

const MINUTE_IN_SECONDS = 60;

export type Mode = 'login' | 'register' | 'reset-send' | 'reset-verify' | 'set-password';
export type OtpStrategy = 'email' | 'phone';

export type FormState = {
  identifier: string;
  password: string;
  resetIdentifier: string;
  resetCode: string;
  newPassword: string;
  regPassword: string;
  regPasswordConfirm: string;
};

export type FieldKey = keyof FormState;
export type FieldErrors = Partial<Record<FieldKey, string | null>>;

export type UiState = {
  showPassword: boolean;
  showNewPassword: boolean;
  showRegPassword: boolean;
  consentChecked: boolean;
  feedbackOpen: boolean;
  privacyDialogOpen: boolean;
  isSubmitted: boolean;
};

const initialForm: FormState = {
  identifier: '',
  password: '',
  resetIdentifier: '',
  resetCode: '',
  newPassword: '',
  regPassword: '',
  regPasswordConfirm: ''
};

const initialUi: UiState = {
  showPassword: false,
  showNewPassword: false,
  showRegPassword: false,
  consentChecked: false,
  feedbackOpen: false,
  privacyDialogOpen: false,
  isSubmitted: false
};

const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
const isValidPhone = (val: string) => isValidRuPhone(val);

const validatePassword = (val: string): string | null => {
  if (val.length < 4) return 'Минимум 4 символа';
  // if (!/[a-zA-Zа-яА-Я]/.test(val)) return 'Должна быть хотя бы одна буква';
  // if (!/\d/.test(val)) return 'Должна быть хотя бы одна цифра';
  return null;
};

export function useAuthForm(initialMode: Mode = 'register') {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [otpStrategy, setOtpStrategy] = useState<OtpStrategy>('phone');
  const [loading, setLoading] = useState(false);
  const [counter, setCounter] = useState(MINUTE_IN_SECONDS);
  const [form, setForm] = useState<FormState>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ui, setUi] = useState<UiState>(initialUi);
  const [pendingAuth, setPendingAuth] = useState<{
    accessToken: string;
    refreshToken: string;
    userId: string;
  } | null>(null);
  const navigate = useNavigate();

  const updateForm = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const updateUi = (patch: Partial<UiState>) => setUi((u) => ({ ...u, ...patch }));
  const setFieldError = (key: FieldKey, value: string | null) =>
    setFieldErrors((fe) => ({ ...fe, [key]: value }));
  const clearErrors = () => {
    setFieldErrors({});
    setSubmitError(null);
  };

  useEffect(() => {
    userApi
      .getAuthConfig()
      .then((cfg) => setOtpStrategy(cfg.otpStrategy))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!ui.isSubmitted) return;
    const interval = setInterval(() => {
      setCounter((c) => (c === 0 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [ui.isSubmitted]);

  const isValidIdentifier = (val: string) =>
    otpStrategy === 'phone' ? isValidPhone(val) : isValidEmail(val);

  const fieldLabel = otpStrategy === 'phone' ? 'Телефон' : 'Email';
  const fieldAutoComplete = otpStrategy === 'phone' ? 'tel' : 'email';
  const fieldPlaceholder = otpStrategy === 'phone' ? '+7 (900) 000-00-00' : 'example@mail.ru';
  const fieldInvalidError =
    otpStrategy === 'phone' ? 'Введите корректный номер телефона' : 'Введите корректный email';
  const otpSentHint =
    otpStrategy === 'phone'
      ? `Мы пришлём четырёхзначный код на номер ${form.identifier}`
      : `Мы пришлём четырёхзначный код на почту ${form.identifier}`;

  const tabValue = mode === 'login' ? 0 : 1;
  const isResetMode = mode === 'reset-send' || mode === 'reset-verify' || mode === 'set-password';

  /**
   * Поднимает сессию по выданным токенам и редиректит:
   * новый пользователь без имени → профиль, иначе → главная.
   */
  const loginAndRedirect = async (
    accessToken: string,
    refreshToken: string,
    userId: string | undefined,
    options?: { replace?: boolean }
  ) => {
    await personalDataStore.login(accessToken, refreshToken, userId, (user) => {
      navigate(!user.firstName ? `${PATHS.profile}?force=true` : PATHS.main, options);
    });
  };

  // ─── Identifier field ──────────────────────────────────────────────

  const handleIdentifierChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateForm({ identifier: e.target.value });
    setFieldError('identifier', null);
    setSubmitError(null);
  };

  const handleIdentifierBlur = () => {
    if (form.identifier.length === 0) {
      setFieldError('identifier', null);
      return;
    }
    setFieldError('identifier', isValidIdentifier(form.identifier) ? null : fieldInvalidError);
  };

  // ─── Register flow ─────────────────────────────────────────────────

  const handleSendOtp = async () => {
    if (!form.identifier) return;
    if (!isValidIdentifier(form.identifier)) {
      setFieldError('identifier', fieldInvalidError);
      return;
    }
    setLoading(true);
    setSubmitError(null);
    try {
      await userApi.sendOtp(form.identifier, otpStrategy);
      updateUi({ isSubmitted: true });
      setCounter(MINUTE_IN_SECONDS);
    } catch (e: any) {
      const status = e?.response?.status;
      setSubmitError(
        status === 409 ? 'Пользователь уже зарегистрирован в системе' : 'Произошла ошибка!'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    setLoading(true);
    setSubmitError(null);
    try {
      const res = await userApi.verifydOtp(form.identifier, code, otpStrategy);
      setPendingAuth({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        userId: res.user.id?.toString() ?? ''
      });
      setMode('set-password');
    } catch {
      setSubmitError('Указан неверный код!');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!pendingAuth) return;
    const pwdErr = validatePassword(form.regPassword);
    if (pwdErr) {
      setFieldError('regPassword', pwdErr);
      return;
    }
    if (form.regPassword !== form.regPasswordConfirm) {
      setFieldError('regPasswordConfirm', 'Пароли не совпадают');
      return;
    }
    setLoading(true);
    clearErrors();
    try {
      localStorage.setItem('accessToken', pendingAuth.accessToken);
      await userApi.updateUser(pendingAuth.userId, { password: form.regPassword });
      await loginAndRedirect(
        pendingAuth.accessToken,
        pendingAuth.refreshToken,
        pendingAuth.userId
      );
    } catch {
      setSubmitError('Произошла ошибка, попробуйте снова');
      localStorage.removeItem('accessToken');
    } finally {
      setLoading(false);
    }
  };

  // ─── Login flow ────────────────────────────────────────────────────

  const handleLogin = async () => {
    if (!form.identifier || !form.password) return;
    setLoading(true);
    clearErrors();
    try {
      const res = await userApi.login(
        otpStrategy === 'email' ? form.identifier : undefined,
        otpStrategy === 'phone' ? form.identifier : undefined,
        form.password
      );
      await loginAndRedirect(res.accessToken, res.refreshToken, res.user.id?.toString());
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 401) {
        setSubmitError(`Неверный ${fieldLabel.toLowerCase()} или пароль`);
      } else if (
        Array.isArray(data?.message) &&
        data.message.some((i: string) => i.includes('Password'))
      ) {
        setSubmitError('Неверный пароль');
      } else {
        setSubmitError('Произошла ошибка!');
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Reset password flow ───────────────────────────────────────────

  const handleSendResetOtp = async () => {
    if (!form.resetIdentifier || !isValidIdentifier(form.resetIdentifier)) {
      setFieldError('resetIdentifier', fieldInvalidError);
      return;
    }
    setLoading(true);
    clearErrors();
    try {
      await userApi.sendResetOtp(form.resetIdentifier, otpStrategy);
      setMode('reset-verify');
    } catch (e: any) {
      const status = e?.response?.status;
      setSubmitError(status === 400 ? 'Пользователь не найден' : 'Произошла ошибка!');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!form.resetCode || form.resetCode.length < 4) {
      setFieldError(
        'resetCode',
        otpStrategy === 'phone' ? 'Введите код из SMS' : 'Введите код из письма'
      );
      return;
    }
    const pwdErr = validatePassword(form.newPassword);
    if (pwdErr) {
      setFieldError('newPassword', pwdErr);
      return;
    }
    setLoading(true);
    clearErrors();
    try {
      await userApi.confirmResetPassword({
        identifier: form.resetIdentifier,
        strategy: otpStrategy,
        code: form.resetCode,
        newPassword: form.newPassword
      });
      setMode('login');
      updateForm({
        identifier: form.resetIdentifier,
        password: '',
        resetCode: '',
        newPassword: '',
        resetIdentifier: ''
      });
    } catch (e: unknown) {
      const msg = (e as AxiosError<{ message?: string[] }>).response?.data?.message;
      if (Array.isArray(msg) && msg[0]?.includes?.('Password')) {
        setFieldError('newPassword', 'Пароль должен содержать как минимум одну букву и одну цифру.');
      } else {
        setSubmitError('Неверный или истёкший код');
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Navigation between modes ──────────────────────────────────────

  const handleTabChange = (_: unknown, value: number) => {
    setMode(value === 0 ? 'login' : 'register');
    updateForm({ identifier: '', password: '' });
    clearErrors();
    updateUi({ isSubmitted: false, consentChecked: false });
  };

  const goToResetSend = () => {
    setMode('reset-send');
    updateForm({ resetIdentifier: form.identifier });
    clearErrors();
  };

  const backToLogin = () => {
    setMode('login');
    clearErrors();
    updateForm({ resetIdentifier: '', resetCode: '', newPassword: '' });
  };

  const backToRegister = () => {
    setMode('register');
    updateUi({ isSubmitted: false });
    setPendingAuth(null);
    updateForm({ regPassword: '', regPasswordConfirm: '' });
    clearErrors();
  };

  return {
    mode,
    otpStrategy,
    loading,
    counter,
    form,
    fieldErrors,
    submitError,
    ui,
    // derived
    fieldLabel,
    fieldAutoComplete,
    fieldPlaceholder,
    otpSentHint,
    tabValue,
    isResetMode,
    // updaters
    updateForm,
    updateUi,
    setFieldError,
    setSubmitError,
    validatePassword,
    loginAndRedirect,
    isValidIdentifier,
    fieldInvalidError,
    // identifier handlers
    handleIdentifierChange,
    handleIdentifierBlur,
    // actions
    handleSendOtp,
    handleVerifyOtp,
    handleSetPassword,
    handleLogin,
    handleSendResetOtp,
    handleConfirmReset,
    handleTabChange,
    goToResetSend,
    backToLogin,
    backToRegister
  };
}

export type AuthForm = ReturnType<typeof useAuthForm>;
